import React, { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";

// Component scope - Constants and static data
const SELECT_VARIANTS = ["outlined", "filled", "standard"];
const SELECT_SIZES = ["small", "medium"];

/**
 * App Select Component - Wrapper for MUI Select
 * @component
 * @param {Object} props - Component props
 * @returns {React.ReactElement} AppSelectComponent
 */
const AppSelectComponent = ({
  variant = "outlined",
  size = "medium",
  label,
  value,
  onChange,
  options = [],
  error = false,
  helperText,
  required = false,
  disabled = false,
  fullWidth = false,
  placeholder,
  InputLabelProps,
  MenuProps: customMenuProps,
  sx,
  ...props
}) => {
  // States, refs, selectors, RTK Query hooks
  const selectRef = useRef(null);
  const [menuAnchorOrigin, setMenuAnchorOrigin] = useState({
    vertical: 'bottom',
    horizontal: 'left'
  });
  const [menuTransformOrigin, setMenuTransformOrigin] = useState({
    vertical: 'top',
    horizontal: 'left'
  });

  // Other functions and logic
  const handleMenuOpen = useCallback(() => {
    // Use setTimeout to ensure DOM is ready and ref is accessible
    setTimeout(() => {
      if (!selectRef.current) return;

      const selectElement = selectRef.current;
      const rect = selectElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate available space below and above
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Estimate menu height (approximately 48px per item + padding)
      // Account for maxHeight of 300px in MenuProps
      const itemHeight = 48;
      const menuPadding = 16;
      const estimatedMenuHeight = Math.min(options.length * itemHeight + menuPadding, 300);

      // Add buffer zone to account for buttons, other form fields, and viewport edges
      // Increased buffer to prevent overlap with buttons (typically 48-56px) and add safety margin
      const buttonBuffer = 80; // Account for buttons and other UI elements below
      const edgeBuffer = 16; // Safety margin from viewport edges
      const minRequiredSpace = estimatedMenuHeight + buttonBuffer + edgeBuffer;

      // Determine if we should open upward
      // Open upward if:
      // 1. Not enough space below AND enough space above, OR
      // 2. Space above is significantly more than space below (prefer upward when more space available above)
      const shouldOpenUpward =
        (spaceBelow < minRequiredSpace && spaceAbove >= minRequiredSpace) ||
        (spaceAbove > spaceBelow + 100 && spaceAbove >= estimatedMenuHeight && spaceBelow < estimatedMenuHeight + buttonBuffer);

      if (shouldOpenUpward) {
        setMenuAnchorOrigin({
          vertical: 'top',
          horizontal: 'left'
        });
        setMenuTransformOrigin({
          vertical: 'bottom',
          horizontal: 'left'
        });
      } else {
        // Default: open downward
        setMenuAnchorOrigin({
          vertical: 'bottom',
          horizontal: 'left'
        });
        setMenuTransformOrigin({
          vertical: 'top',
          horizontal: 'left'
        });
      }
    }, 0);
  }, [options.length]);

  // Return/rendering
  return (
    <FormControl
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      error={error}
      required={required}
      disabled={disabled}
      sx={sx}
      {...props}
    >
      {label && <InputLabel {...InputLabelProps}>{label}</InputLabel>}
      <Select
        ref={selectRef}
        autoFocus={props.autoFocus}
        value={value !== null && value !== undefined ? value : ""}
        onChange={onChange}
        label={label}
        displayEmpty
        disabled={disabled}
        onOpen={handleMenuOpen}
        renderValue={(selected) => {
          if (selected === "" || selected === null || selected === undefined) {
            if (placeholder) {
              return (
                <span style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "14px" }}>
                  {placeholder}
                </span>
              );
            }
            return null;
          }
          // Handle numeric values properly (0 is a valid value)
          const selectedOption = options.find(opt => {
            // Use strict equality to handle both string and number comparisons
            return opt.value === selected || String(opt.value) === String(selected);
          });
          return selectedOption ? selectedOption.label : selected;
        }}
        sx={{
          backgroundColor: "#13172A",
          color: "white"
        }}
        MenuProps={{
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableScrollLock: false,
          anchorOrigin: customMenuProps?.anchorOrigin || menuAnchorOrigin,
          transformOrigin: customMenuProps?.transformOrigin || menuTransformOrigin,
          ...customMenuProps,
          PaperProps: {
            sx: {
              backgroundColor: "#13172A",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
              maxHeight: 300,
              "& .MuiMenuItem-root": {
                backgroundColor: "#13172A",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  },
                },
              },
            },
            ...customMenuProps?.PaperProps,
          },
        }}
      >
        {placeholder && (
          <MenuItem value="" disabled sx={{ display: "none" }}>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              backgroundColor: "#13172A",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography
          variant="caption"
          color={error ? "error" : "textSecondary"}
          sx={{ mt: 0.5 }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

AppSelectComponent.propTypes = {
  variant: PropTypes.oneOf(SELECT_VARIANTS),
  size: PropTypes.oneOf(SELECT_SIZES),
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  sx: PropTypes.object,
  InputLabelProps: PropTypes.object,
  MenuProps: PropTypes.object,
};

AppSelectComponent.defaultProps = {
  variant: "outlined",
  size: "medium",
  options: [],
  error: false,
  required: false,
  disabled: false,
  fullWidth: false,
};

export default AppSelectComponent;
